import React, { useState, useRef } from "react";
import axiosInstance from "../../services/FetchNodeServices";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const TxtProduct = () => {
  const [jsonData, setJsonData] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== "text/plain") {
      toast.error("Please upload a valid .txt file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const parsedJson = convertTxtToJson(text);
      setJsonData(parsedJson);
      toast.success(`✅ File parsed! ${parsedJson.length} products found.`, { autoClose: 2000 });
    };
    reader.readAsText(file);
  };

  const convertTxtToJson = (text) => {
    const lines = text.trim().split("\n");
    const headers = lines[0].split("##").map((h) => h.trim());
    return lines.slice(1).map((line) => {
      const values = line.split("##").map((v) => v.trim());
      const obj = {};
      headers.forEach((key, index) => { obj[key] = values[index] ?? ""; });
      return obj;
    });
  };

  const CHUNK_SIZE = 2500;

  const uploadInChunks = async (data) => {
    const totalChunks = Math.ceil(data.length / CHUNK_SIZE);
    let totalProcessed = 0;

    for (let i = 0; i < data.length; i += CHUNK_SIZE) {
      const chunk = data.slice(i, i + CHUNK_SIZE);
      const currentChunk = i / CHUNK_SIZE + 1;
      const percent = Math.round((currentChunk / totalChunks) * 100);

      Swal.update({
        html: `
          <p style="font-size: 15px; color: #555; margin-bottom:10px;">
            Uploading chunk <strong>${currentChunk}</strong> of <strong>${totalChunks}</strong>
          </p>
          <div class="progress" style="height: 20px; border-radius:10px;">
            <div class="progress-bar progress-bar-striped progress-bar-animated bg-success" 
              role="progressbar" style="width: ${percent}%; font-size:12px;">
              ${percent}%
            </div>
          </div>
          <p style="font-size: 12px; color: #888; margin-top:10px;">
            🔄 Operation: <strong>UPSERT</strong> — New products created, existing ones updated
          </p>
          <p style="font-size: 12px; color: #888;">
            📦 Products processed so far: <strong>${Math.min(i + CHUNK_SIZE, data.length)}</strong> / ${data.length}
          </p>
        `,
      });

      try {
        await axiosInstance.post(
          "/api/v1/product/multiple-product",
          { products: chunk },
          { headers: { "Content-Type": "application/json; charset=utf-8" } }
        );
        totalProcessed += chunk.length;
      } catch (error) {
        console.error(`Chunk ${currentChunk} upload failed`, error);
        throw error;
      }
    }
    return totalProcessed;
  };

  const handleSubmit = async () => {
    if (!jsonData.length) {
      toast.error("No data to upload. Please upload a .txt file first.");
      return;
    }
    if (!jsonData[0]?.PRODUCTS_NAME) { toast.error("PRODUCTS_NAME field is required."); return; }
    if (!jsonData[0]?.PRODUCTS_ID) { toast.error("PRODUCTS_ID field is required."); return; }
    if (!jsonData[0]?.PRODUCTS_MRP_IN_DOLLAR) { toast.error("PRODUCTS_MRP_IN_DOLLAR field is required."); return; }
    if (!jsonData[0]?.PRODUCTS_SP_IN_DOLLAR) { toast.error("PRODUCTS_SP_IN_DOLLAR field is required."); return; }
    if (!jsonData[0]?.ISBN13) { toast.error("ISBN13 field is required."); return; }

    Swal.fire({
      title: "<strong>📦 Uploading Products</strong>",
      html: `
        <p style="font-size: 15px; color: #555; margin-bottom:10px;">
          Starting upload of <strong>${jsonData.length}</strong> products...
        </p>
        <div class="progress" style="height: 20px; border-radius:10px;">
          <div class="progress-bar progress-bar-striped progress-bar-animated bg-success" 
            style="width: 5%;">5%</div>
        </div>
        <p style="font-size: 12px; color: #888; margin-top:10px;">
          🔄 Operation: UPSERT — New products created, existing ones updated
        </p>
      `,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
      customClass: {
        popup: "rounded-xl shadow-lg border border-[#da7921]",
        title: "text-lg text-[#da7921]",
      },
    });

    try {
      setDisabled(true);
      const totalProcessed = await uploadInChunks(jsonData);

      Swal.close();
      Swal.fire({
        icon: "success",
        title: "✅ Products Uploaded!",
        html: `
          <div style="text-align:left; background:#f8f9fa; padding:15px; border-radius:8px;">
            <p style="font-size:15px; color:#444; margin-bottom:10px;">
              <strong>${totalProcessed}</strong> products processed successfully!
            </p>
            <hr/>
            <p style="font-size:13px; color:#666; font-weight:bold; margin-bottom:8px;">
              📋 What happened:
            </p>
            <ul style="font-size:13px; color:#555; padding-left:20px; margin-bottom:0;">
              <li>✅ <strong>New products</strong> → <span style="color:green; font-weight:bold;">CREATED</span></li>
              <li>🔄 <strong>Existing products</strong> (matched by PRODUCTS_ID) → <span style="color:blue; font-weight:bold;">UPDATED</span></li>
              <li>📝 Fields updated: Title, Author, Publisher, Price, Discount, ISBN, Images</li>
              <li>⚠️ <strong>Stock was NOT changed</strong> by this upload</li>
            </ul>
          </div>
        `,
        confirmButtonText: "Done ✅",
        confirmButtonColor: "#da7921",
        background: "#f0fff4",
      });

      setDisabled(false);
      setJsonData([]);
      // ✅ Reset file input
      if (fileInputRef.current) fileInputRef.current.value = "";

    } catch (error) {
      setDisabled(false);
      Swal.close();
      toast.error("❌ Upload failed. Please try again.");
    }
  };

  return (
    <div className="container mt-5">
      <div className="card shadow">
        <div className="card-body">
          <div className="p-4 mb-2 text-start">
            <h4 className="card-title mb-3">Upload Products (Txt)</h4>
            <p>File Name: <span style={{ color: "red", fontSize: "18px" }}>Originalproduct.txt</span></p>

            {/* ℹ️ How it works */}
            <div style={{
              background: "#e8f4fd",
              border: "1px solid #3498db",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "12px"
            }}>
              <p style={{ fontWeight: "bold", color: "#2980b9", marginBottom: "8px" }}>
                ℹ️ How this upload works:
              </p>
              <ul style={{ fontSize: "13px", color: "#555", paddingLeft: "20px", marginBottom: "0" }}>
                <li>🔍 <strong>Matches by:</strong> PRODUCTS_ID</li>
                <li>✅ <strong>New product?</strong> → <span style={{ color: "green", fontWeight: "bold" }}>CREATES</span> it in database</li>
                <li>🔄 <strong>Existing product?</strong> → <span style={{ color: "blue", fontWeight: "bold" }}>UPDATES</span> all fields</li>
                <li>📝 <strong>Fields updated:</strong> Title, Author, Publisher, Price, Discount, ISBN, Images</li>
                <li>⚠️ <strong>Stock is NOT affected</strong> by this upload</li>
                <li>📦 <strong>Uploads in chunks</strong> of 2500 products per request</li>
              </ul>
            </div>

            {/* ⚠️ Required Fields */}
            <div style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "8px",
              padding: "12px",
            }}>
              <p style={{ fontWeight: "bold", color: "#856404", marginBottom: "8px" }}>
                ⚠️ Required columns in TXT file:
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {["PRODUCTS_ID", "PRODUCTS_NAME", "ISBN13", "PRODUCTS_MRP_IN_DOLLAR", "PRODUCTS_SP_IN_DOLLAR"].map(field => (
                  <span key={field} style={{
                    background: "#ffc107",
                    color: "#333",
                    padding: "3px 10px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "bold"
                  }}>{field}</span>
                ))}
              </div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".txt"
            onChange={handleFileChange}
            className="form-control mb-3"
          />

          {jsonData.length > 0 && (
            <div className="alert alert-success">
              📊 <strong>{jsonData.length}</strong> products ready to upload
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={disabled || jsonData.length === 0}
            className="btn"
            style={{ backgroundColor: "#6f42c1", color: "white" }}
          >
            {disabled ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Uploading...
              </>
            ) : "Submit Products"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TxtProduct;
